import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { logger } from '../utils/logger'
import { authService, User, LoginCredentials, RegisterData, ProfileUpdate } from '../services/authService'
import { analyticsService } from '../services/analyticsService'

interface AuthContextType {
  // State
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  isInitialized: boolean

  // Actions
  login: (credentials: LoginCredentials) => Promise<{ user: User } | { error: string }>
  register: (data: RegisterData) => Promise<User | { pendingApproval: true } | null>
  logout: () => Promise<void>
  updateProfile: (updates: ProfileUpdate) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  resetPassword: (token: string, password: string, confirmPassword: string) => Promise<void>
  verifyEmail: (token: string) => Promise<void>
  clearError: () => void
  refreshSession: () => Promise<void>
  
  // Role helpers
  hasRole: (role: 'consumer' | 'business' | 'admin') => boolean
  hasAnyRole: (roles: ('consumer' | 'business' | 'admin')[]) => boolean
  canAccess: (resource: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Initialize auth state with better session management
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true)
        
        // Small delay to prevent race conditions
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // Check if user is already logged in
        if (authService.isAuthenticated()) {
          // Try to get current user first
          let currentUser = await authService.getCurrentUser()
          
          // If that fails, try to refresh the token
          if (!currentUser) {
            const refreshResult = await authService.refreshAuth()
            if (refreshResult.success) {
              currentUser = refreshResult.user || null
            }
          }
          
          if (currentUser && currentUser.email) {
            // Transform backend user format to frontend format
            const user = {
              ...currentUser,
              name: currentUser.name || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'User'
            }
            setUser(user)
            // Track successful session restoration
            analyticsService.setUserId(user.id)
          } else {
            // If both fail, clear auth but don't redirect immediately
            await authService.logout()
            setUser(null)
          }
        }
      } catch (err) {
        logger.error('Auth initialization error:', err)
        // Clear invalid auth data
        await authService.logout()
        setUser(null)
      } finally {
        setIsLoading(false)
        setIsInitialized(true)
      }
    }

    initializeAuth()
  }, [])

  // Auto-refresh session periodically
  const isAuthenticated = !!user

  useEffect(() => {
    if (!isAuthenticated || !user) return

    const refreshInterval = setInterval(async () => {
      try {
        await refreshSession()
      } catch (err) {
        logger.error('Auto-refresh session failed:', err)
        // Don't logout on refresh failure, just log it
      }
    }, 30 * 60 * 1000) // Refresh every 30 minutes

    return () => clearInterval(refreshInterval)
  }, [isAuthenticated, user])

  const login = async (credentials: LoginCredentials) => {
    try {
      setError(null)
      setIsLoading(true)
      
      const response = await authService.login(credentials)
      console.log('AuthContext login response:', response)
      if (response.user && response.user.email) {
        // Transform backend user format to frontend format
        const user = {
          ...response.user,
          name: response.user.name || `${response.user.first_name || response.user.first_name || ''} ${response.user.last_name || response.user.last_name || ''}`.trim() || 'User'
        }
        console.log('AuthContext transformed user:', user)
        setUser(user)
        return { user }
      } else {
        const errorMessage =
          response.error || response.message || 'Login failed - no user data received'
        setUser(null)
        setError(errorMessage)
        return { error: errorMessage }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (data: RegisterData): Promise<User | { pendingApproval: true } | null> => {
    try {
      setError(null)
      setIsLoading(true)
      
      const response = await authService.register(data)
      if (response.pendingApproval) {
        setUser(null)
        const role = data.role || 'consumer'
        analyticsService.trackRegistration(role, true)
        return { pendingApproval: true }
      }
      if (response.user && response.user.email) {
        // Transform backend user format to frontend format
        const user = {
          ...response.user,
          name: response.user.name || `${response.user.first_name || response.user.first_name || ''} ${response.user.last_name || response.user.last_name || ''}`.trim() || 'User'
        }
        setUser(user)
        analyticsService.trackRegistration(data.role || 'consumer', true)
        analyticsService.setUserId(user.id)
        return user
      } else {
        setUser(null)
        return null
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed'
      setError(errorMessage)
      
      analyticsService.trackRegistration('consumer', false)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      setError(null)
      setIsLoading(true)
      
      await authService.logout()
      setUser(null)
    } catch (err) {
      logger.error('Logout error:', err)
      // Still clear local state even if API call fails
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const updateProfile = async (updates: ProfileUpdate) => {
    try {
      setError(null)
      setIsLoading(true)
      
      const updatedUser = await authService.updateProfile(updates)
      setUser(updatedUser)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Profile update failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      setError(null)
      setIsLoading(true)
      
      await authService.changePassword(currentPassword, newPassword)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Password change failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const requestPasswordReset = async (email: string) => {
    try {
      setError(null)
      setIsLoading(true)
      
      await authService.requestPasswordReset(email)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Password reset request failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const resetPassword = async (token: string, password: string, confirmPassword: string) => {
    try {
      setError(null)
      setIsLoading(true)
      
      await authService.resetPassword({ token, password, confirmPassword })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Password reset failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const verifyEmail = async (token: string) => {
    try {
      setError(null)
      setIsLoading(true)
      
      await authService.verifyEmail(token)
      
      // Update user verification status
      if (user) {
        setUser({ ...user, isVerified: true })
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Email verification failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const clearError = () => {
    setError(null)
  }

  const refreshSession = async () => {
    try {
      if (!authService.isAuthenticated()) return
      
      // Try to refresh the token first
      const refreshResult = await authService.refreshAuth()
      if (refreshResult.success) {
        if (refreshResult.user && refreshResult.user.email) {
          // Transform backend user format to frontend format
          const user = {
            ...refreshResult.user,
            name: refreshResult.user.name || `${refreshResult.user.first_name || refreshResult.user.first_name || ''} ${refreshResult.user.last_name || refreshResult.user.last_name || ''}`.trim() || 'User'
          }
          setUser(user)
        } else {
          setUser(null)
        }
        return
      }
      
      // If refresh fails, try to get current user
      const currentUser = await authService.getCurrentUser()
      if (currentUser && currentUser.email) {
        // Transform backend user format to frontend format
        const user = {
          ...currentUser,
          name: currentUser.name || `${currentUser.first_name || currentUser.first_name || ''} ${currentUser.last_name || currentUser.last_name || ''}`.trim() || 'User'
        }
        setUser(user)
      } else {
        // If both fail, logout the user
        await authService.logout()
        setUser(null)
      }
    } catch (err) {
      logger.error('Session refresh error:', err)
      // If refresh fails completely, logout the user
      await authService.logout()
      setUser(null)
    }
  }

  // Role helpers
  const hasRole = (role: 'consumer' | 'business' | 'admin'): boolean => {
    return isAuthenticated && user?.role === role
  }

  const hasAnyRole = (roles: ('consumer' | 'business' | 'admin')[]): boolean => {
    return isAuthenticated && user && roles.includes(user.role)
  }

  const canAccess = (resource: string): boolean => {
    if (!isAuthenticated || !user) return false

    const permissions = {
      consumer: [
        'dashboard',
        'profile',
        'favorites',
        'reviews',
        'bookings',
        'adoption',
        'articles'
      ],
      business: [
        'business-dashboard',
        'business-profile',
        'listings',
        'bookings',
        'analytics',
        'settings',
        'adoption',
        'articles'
      ],
      admin: [
        'admin-dashboard',
        'users',
        'businesses',
        'adoptions',
        'articles',
        'analytics',
        'settings',
        'reports'
      ]
    }

    return permissions[user.role]?.includes(resource) || false
  }

  const value: AuthContextType = {
    // State
    user,
    isAuthenticated: !!user,
    isLoading,
    isInitialized,
    error,

    // Actions
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    requestPasswordReset,
    resetPassword,
    verifyEmail,
    clearError,
    refreshSession,
    
    // Role helpers
    hasRole,
    hasAnyRole,
    canAccess,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Higher-order component for protected routes
export function withAuth<P extends object>(
  Component: React.ComponentType<P>
) {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuth()

    if (isLoading) {
      return <div>Loading...</div>
    }

    if (!isAuthenticated) {
      return <div>Please log in to access this page.</div>
    }

    return <Component {...props} />
  }
}
